import { useEffect, useRef, useState } from 'react';
import { Checkbox, Collapse, Input, InputNumber, Select, Space, Typography } from 'antd';
import { HDate } from '@hebcal/core';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useLingui } from '@lingui/react';
import { CloseCircleFilled } from '@ant-design/icons';

const { Text } = Typography;

const monthNamesHE = ['Tishrei', 'Cheshvan', 'Kislev', 'Tevet', 'Sh\'vat', 'Adar', 'Adar I', 'Adar II', 'Nisan', 'Iyyar', 'Sivan', 'Tamuz', 'Av', 'Elul'];

const isValidDate = date => date instanceof Date && Number.isFinite(date.getTime());
const storedDate = value => {
    if (value === null || value === undefined || value === '' ||
        !(value instanceof Date || typeof value === 'string' || typeof value === 'number')) return null;
    const date = new Date(value);
    return isValidDate(date) ? date : null;
};
const isAfterDark = date => date.getHours() >= 23;
const dateKey = value => {
    const date = storedDate(value);
    return date ? `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}:${isAfterDark(date)}` : 'empty';
};
const dateToString = date => `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).padStart(4, '0')}`;
const stringToDate = value => {
    if (typeof value !== 'string' || !/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return null;
    const [day, month, year] = value.split('/').map(Number);
    const date = new Date(0);
    date.setFullYear(year, month - 1, day);
    date.setHours(11, 0, 0, 0);
    return year > 0 && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};
const emptyState = () => ({inputValue: null, afterDark: false, hdateDay: null, hdateMonth: null, hdateYear: null});
const stateFromDate = date => {
    if (!date) return emptyState();
    const hdate = new HDate(date);
    return {inputValue: dateToString(date), afterDark: isAfterDark(date), hdateDay: hdate.getDate(), hdateMonth: hdate.getMonthName(), hdateYear: hdate.getFullYear()};
};
const formater = value => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join('/');
};

export default function DdnHeField({ value, onChange, isMobile, isClearable }) {
    const {i18n} = useLingui();
    const [state, setState] = useState(() => stateFromDate(storedDate(value)));
    const {inputValue, afterDark, hdateDay, hdateMonth, hdateYear} = state;
    const valueRef = useRef(value);
    valueRef.current = value;
    const incomingKey = dateKey(value);

    // Hydration/restoration never emits a change. Equivalent Date/ISO values
    // must also leave partially typed input alone when the parent rerenders.
    useEffect(() => {
        setState(stateFromDate(storedDate(valueRef.current)));
    }, [incomingKey]);

    const emit = next => {
        if (dateKey(next) !== dateKey(valueRef.current)) onChange?.(next);
    };
    const handleClear = () => {
        setState(emptyState());
        emit(null);
    };
    const commitDate = (date, nextAfterDark = afterDark) => {
        date.setHours(nextAfterDark ? 23 : 11, 0, 0, 0);
        setState(stateFromDate(date));
        emit(date);
    };
    const handleInputChange = event => {
        const nextInput = formater(event.target.value);
        if (!nextInput) return handleClear();
        const date = stringToDate(nextInput);
        if (date) commitDate(date);
        else setState(previous => ({...previous, inputValue: nextInput}));
    };
    const handleAfterDarkChange = event => {
        const nextAfterDark = event.target.checked;
        const date = stringToDate(inputValue);
        if (date) commitDate(date, nextAfterDark);
        else setState(previous => ({...previous, afterDark: nextAfterDark}));
    };
    const handleHebrewChange = (field, nextValue) => {
        const next = {...state, [field]: nextValue};
        setState(next);
        if (!next.hdateDay || !next.hdateMonth || !next.hdateYear) return;
        try {
            const date = new HDate(next.hdateDay, next.hdateMonth, next.hdateYear).greg();
            if (isValidDate(date)) commitDate(date);
        } catch (_) {
            // An incomplete Hebrew date stays editable without changing the form.
        }
    };

    return (
        <Space size={"large"} direction="row" align="start" wrap>
            <Input
                placeholder={t(i18n)`20/05/2005`}
                value={inputValue}
                onChange={handleInputChange}
                status={(inputValue?.length === 10 && !isValidDate(stringToDate(inputValue))) ? "error" : undefined}
                suffix={
                    isClearable && inputValue ? (
                        <CloseCircleFilled
                            onClick={handleClear}
                            style={{ cursor: 'pointer', color: '#999' }}
                        />
                    ) : null
                }
            />

            <Collapse>
                <Collapse.Panel key={0} header={<Trans>Convertisseur hébraïque</Trans>}>
                    <Space size={"small"} align={"center"} wrap direction={isMobile ? "vertical" : "horizontal"}>
                        <InputNumber
                            min={1}
                            max={30}
                            style={{ width: 150 }}
                            addonAfter={afterDark ? "+1" : null}
                            placeholder={t(i18n)`Jour hébraïque (1-30)`}
                            value={hdateDay}
                            controls={false}
                            onChange={(v) => handleHebrewChange('hdateDay', v)}
                        />

                        <Select
                            style={{ minWidth: 150 }}
                            placeholder={t(i18n)`Mois hébraïque`}
                            size={"large"}
                            value={hdateMonth}
                            onChange={(v) => handleHebrewChange('hdateMonth', v)}
                            options={monthNamesHE.map(i => ({ value: i, label: i }))}
                        />

                        <InputNumber
                            style={{ minWidth: 150 }}
                            placeholder={t(i18n)`Année hébraïque (5780)`}
                            value={hdateYear}
                            controls={false}
                            onChange={(v) => handleHebrewChange('hdateYear', v)}
                        />

                        <Space>
                            <Checkbox
                                checked={afterDark}
                                onChange={handleAfterDarkChange}
                            >
                                <Trans>Après la nuit (+1 jour hébraïque)</Trans>
                            </Checkbox>
                        </Space>
                    </Space>
                </Collapse.Panel>
            </Collapse>
        </Space>
    );
}
